import { DENSITIES, THEMES, type Density, type Theme } from './tokens.js';

const THEME_KEY = 'aimmo.theme';
const DENSITY_KEY = 'aimmo.density';

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

function isDensity(value: unknown): value is Density {
  return typeof value === 'string' && (DENSITIES as readonly string[]).includes(value);
}

/**
 * Appearance is document state, not React state: it is painted before the first
 * render and read by code that has no component around it. React subscribes to
 * these listeners rather than owning the value.
 */
const listeners = new Set<() => void>();

let theme: Theme = 'light';
let density: Density = 'comfortable';

function notify(): void {
  for (const listener of listeners) listener();
}

/** Subscribe to theme/density changes. Returns the unsubscribe function. */
export function subscribeAppearance(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The theme the user last chose, or `null` if they never chose one. */
export function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(THEME_KEY);
    return isTheme(value) ? value : null;
  } catch {
    // Private mode, blocked storage: fall back to the system preference.
    return null;
  }
}

export function storedDensity(): Density | null {
  try {
    const value = localStorage.getItem(DENSITY_KEY);
    return isDensity(value) ? value : null;
  } catch {
    return null;
  }
}

export function systemTheme(): Theme {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Paint a theme now. Does not persist — `setTheme` does that. */
export function applyTheme(next: Theme): void {
  theme = next;
  document.documentElement.classList.toggle('dark', next === 'dark');
  notify();
}

export function applyDensity(next: Density): void {
  density = next;
  document.documentElement.dataset['density'] = next;
  notify();
}

export function setTheme(next: Theme): void {
  applyTheme(next);
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch {
    // Choice does not survive the reload; the current view is still correct.
  }
}

export function setDensity(next: Density): void {
  applyDensity(next);
  try {
    localStorage.setItem(DENSITY_KEY, next);
  } catch {
    // As above.
  }
}

/** The theme currently painted. Cheap, synchronous, safe during render. */
export function currentTheme(): Theme {
  return theme;
}

export function currentDensity(): Density {
  return density;
}

/**
 * Apply the stored choices, else the system preference, and keep following the
 * system until the user picks a theme. Returns the theme in effect.
 */
export function initTheme(): Theme {
  applyTheme(storedTheme() ?? systemTheme());
  applyDensity(storedDensity() ?? 'comfortable');

  globalThis.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
    if (storedTheme() === null) applyTheme(event.matches ? 'dark' : 'light');
  });

  return theme;
}
